<script lang="ts">
  import { createQuery } from '@tanstack/svelte-query';
  import { Search, Sun, Moon, ChevronRight, Menu } from 'lucide-svelte';
  import { route } from '$lib/state/route.svelte';
  import { session } from '$lib/state/session.svelte';
  import { theme } from '$lib/state/theme.svelte';
  import { palette } from '$lib/state/palette.svelte';
  import { ticketsApi } from '$lib/api/tickets';
  import { docsApi } from '$lib/api/docs';

  const { onMenu }: { onMenu?: () => void } = $props();

  const r = $derived(route.current);
  const admin = $derived(session.isAdmin);

  // Ticket/doc detail routes need their parent project's name for the full
  // breadcrumb trail (design pack: "Projects/Workspace > Project > Ticket|
  // Doc") — same TanStack cache key TicketDetailPage/DocReaderPage already
  // populate, so this is a cache hit in the common case, not an extra
  // round-trip.
  const ticketQuery = createQuery(() => ({
    queryKey: ['ticket', r.tab === 'ticket' ? r.ticketId : -1],
    queryFn: () => ticketsApi.get(r.tab === 'ticket' ? r.ticketId : -1),
    enabled: r.tab === 'ticket'
  }));
  const docQuery = createQuery(() => ({
    queryKey: ['doc', r.tab === 'doc' ? r.docId : -1],
    queryFn: () => docsApi.get(r.tab === 'doc' ? r.docId : -1),
    enabled: r.tab === 'doc'
  }));

  const detailProjectId = $derived(
    r.tab === 'project' ? r.projectId : r.tab === 'ticket' ? ticketQuery.data?.project_id : r.tab === 'doc' ? docQuery.data?.project_id : null
  );
  const projectName = $derived(
    detailProjectId != null ? (session.me?.projects.find((p) => p.id === detailProjectId)?.name ?? null) : null
  );

  // Global Ctrl/Cmd+K toggle (case-insensitive) — design pack §4.
  function onKeydown(e: KeyboardEvent) {
    if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
      e.preventDefault();
      palette.toggle();
    }
  }
</script>

<svelte:window onkeydown={onKeydown} />

<div class="topbar">
  <button type="button" class="icon-btn md:hidden" onclick={onMenu} aria-label="Open navigation">
    <Menu size={20} />
  </button>
  <div class="crumb">
    {#if r.tab === 'overview'}
      <b>Projects</b>
    {:else if r.tab === 'inbox'}
      <b>Ticket inbox</b>
    {:else if r.tab === 'deploys'}
      <b>Deploys</b>
    {:else if r.tab === 'users'}
      <b>Users</b>
    {:else if r.tab === 'settings'}
      <b>Settings</b>
    {:else if r.tab === 'project' || r.tab === 'ticket' || r.tab === 'doc'}
      {#if projectName}
        <span>{admin ? 'Projects' : 'Workspace'}</span>
        <ChevronRight size={14} />
        <b>{projectName}</b>
      {:else if r.tab === 'ticket'}
        <b>Ticket</b>
      {:else if r.tab === 'doc'}
        <b>Doc</b>
      {:else}
        <b>Project</b>
      {/if}
      {#if r.tab === 'ticket' && projectName}
        <ChevronRight size={14} />
        <span>Ticket</span>
      {:else if r.tab === 'doc' && projectName}
        <ChevronRight size={14} />
        <span>Doc</span>
      {/if}
    {/if}
  </div>
  <div class="spacer"></div>
  <button type="button" class="searchbar" onclick={() => palette.toggle()}>
    <Search size={16} />
    <span class="sp">Search tasks & tickets</span>
    <span class="kbd">⌘K</span>
  </button>
  <button type="button" class="icon-btn" title="Toggle theme" onclick={() => theme.toggle()}>
    {#if theme.current === 'dark'}<Sun size={18} />{:else}<Moon size={18} />{/if}
  </button>
</div>
